/**
 * @module SortByPanel/DatasetFieldSort/datasetFieldSortSync
 * @description 字段排序（sortBy=datasetField）A1/B1 同步：引用失效时整组回默认
 *
 * - A1：引用的自定义字段被删 → reset
 * - B1：引用字段在最新 dataTables(+customColumns) 解析不到 → reset（不做同名兜底）
 * - 写入约定：一旦判定失效，**base 与 draft 同时**回默认（MERGE_QB_PATCH）
 * - B1 必须等 `dataTablesSchemaReady`（区分「加载中」与「已加载但为空」）
 */

import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@dbl/pages/Multidimensional/Analysis/interface';
import type { IMetricSortValueItem } from '../../../../types/settings/sortFields';
import { SortByEnum } from '../SortType';
import {
  buildDefaultSortPayload,
  toSortUiMode,
} from '../../../XAxisSetting/XAxisSortBy/utils';
import {
  buildDatasetFieldSelectOptions,
  isCustomDatasetFieldTableCode,
  resolveSelectValueFromSortItem,
} from './utils';

function isDatasetFieldSortMode(sortBy: unknown): boolean {
  return toSortUiMode(String(sortBy ?? '')) === SortByEnum.DatasetFieldSort;
}

function readChartSorts(layer: Record<string, unknown>): IMetricSortValueItem[] {
  return Array.isArray(layer.chartSorts)
    ? (layer.chartSorts as IMetricSortValueItem[])
    : [];
}

/** 合并有效层：draft 覆盖 base（与面板读取一致） */
export function resolveEffectiveDatasetFieldSortLayer(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): Record<string, unknown> {
  return {
    sortBy: draft.sortBy ?? base.sortBy,
    chartSorts:
      'chartSorts' in draft ? draft.chartSorts : base.chartSorts,
  };
}

export function isCustomDatasetFieldSortItem(
  item: IMetricSortValueItem | undefined,
  customColumns?: CustomColumn[],
): boolean {
  if (!item?.key) {
    return false;
  }
  // 上传约定：自定义字段 table_code 为空或 t0
  if (!isCustomDatasetFieldTableCode(item.table_code)) {
    return false;
  }
  if (customColumns) {
    return customColumns.some(
      (col) => (col.name_cn || col.id) === item.key,
    );
  }
  // 无列表时仅能按 table_code 约定判断（A1 仍靠 deletedNames 过滤 key）
  return true;
}

export function matchesDeletedCustomColumnNames(
  item: IMetricSortValueItem | undefined,
  deletedNames: readonly string[],
): boolean {
  if (!isCustomDatasetFieldSortItem(item)) {
    return false;
  }
  const names = deletedNames.filter(Boolean);
  return names.includes(String(item!.key));
}

/**
 * 数据表 schema 是否已确认（Analysis model.dataTablesSchemaReady）。
 * - false：进页尚未 setTableInfo，禁止 B1（避免 customColumns 先到、表后到时误清空）
 * - true：setTableInfo/clearTableInfo 已完成；即便表为空也允许 B1 回退
 */
export function isDatasetFieldSchemaReady(
  dataTablesSchemaReady: boolean | undefined,
): boolean {
  return dataTablesSchemaReady === true;
}

/** @deprecated 使用 isDatasetFieldSchemaReady；保留别名避免外部旧引用断裂 */
export function isDatasetFieldSourceReady(
  _dataTables: IGetTableInfoDto[],
  _customColumns: CustomColumn[],
  dataTablesSchemaReady?: boolean,
): boolean {
  return isDatasetFieldSchemaReady(dataTablesSchemaReady);
}

/** 当前 chartSorts[0] 能否在整表字段 + 自定义字段中解析到（B1，无同名兜底） */
export function isDatasetFieldSortRefResolvable(
  item: IMetricSortValueItem | undefined,
  dataTables: IGetTableInfoDto[],
  customColumns: CustomColumn[],
): boolean {
  if (!item?.key) {
    return false;
  }
  const { metaByValue } = buildDatasetFieldSelectOptions({
    dataTables: dataTables || [],
    customColumns: customColumns || [],
    isEn: false,
    customGroupLabel: '',
  });
  const selectValue = resolveSelectValueFromSortItem(item, metaByValue);
  return !!metaByValue[selectValue];
}

export function shouldResetDatasetFieldSortOnCustomDelete(
  layer: Record<string, unknown>,
  deletedNames: readonly string[],
): boolean {
  if (!isDatasetFieldSortMode(layer.sortBy)) {
    return false;
  }
  const item = readChartSorts(layer)[0];
  return matchesDeletedCustomColumnNames(item, deletedNames);
}

export function shouldResetDatasetFieldSortOnUnresolved(
  layer: Record<string, unknown>,
  dataTables: IGetTableInfoDto[],
  customColumns: CustomColumn[],
  dataTablesSchemaReady: boolean,
): boolean {
  if (!isDatasetFieldSortMode(layer.sortBy)) {
    return false;
  }
  // schema 未确认：不可用「空/半空 meta」判定失效
  if (!isDatasetFieldSchemaReady(dataTablesSchemaReady)) {
    return false;
  }
  const item = readChartSorts(layer)[0];
  if (!item?.key) {
    return false;
  }
  return !isDatasetFieldSortRefResolvable(item, dataTables, customColumns);
}

/**
 * 任一有效配置需回默认时，对 base/draft **同时**返回 reset patch。
 */
export function buildDatasetFieldSortResetBothIfNeeded(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  shouldResetLayer: (layer: Record<string, unknown>) => boolean,
): { base: Record<string, unknown>; draft: Record<string, unknown> } | undefined {
  const effective = resolveEffectiveDatasetFieldSortLayer(base, draft);
  if (
    !shouldResetLayer(effective)
    && !shouldResetLayer(base)
    && !shouldResetLayer(draft)
  ) {
    return undefined;
  }
  const resetPatch = buildDefaultSortPayload();
  return {
    base: { ...resetPatch },
    draft: { ...resetPatch },
  };
}

export function buildDatasetFieldSortResetOnCustomDelete(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  deletedNames: readonly string[],
): { base: Record<string, unknown>; draft: Record<string, unknown> } | undefined {
  return buildDatasetFieldSortResetBothIfNeeded(base, draft, layer =>
    shouldResetDatasetFieldSortOnCustomDelete(layer, deletedNames),
  );
}

export function buildDatasetFieldSortResetOnUnresolved(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  dataTables: IGetTableInfoDto[],
  customColumns: CustomColumn[],
  dataTablesSchemaReady: boolean,
): { base: Record<string, unknown>; draft: Record<string, unknown> } | undefined {
  return buildDatasetFieldSortResetBothIfNeeded(base, draft, layer =>
    shouldResetDatasetFieldSortOnUnresolved(
      layer,
      dataTables,
      customColumns,
      dataTablesSchemaReady,
    ),
  );
}

/** 单层配置（含 save_column_infos 内层）若需 A1/B1 则合并回默认 */
export function pruneDatasetFieldSortLayerIfNeeded(
  layer: Record<string, unknown>,
  shouldResetLayer: (layer: Record<string, unknown>) => boolean,
): Record<string, unknown> | undefined {
  if (!shouldResetLayer(layer)) {
    return undefined;
  }
  return {
    ...layer,
    ...buildDefaultSortPayload(),
  };
}
